import Box from "@mui/material/Box";
import { Chip, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material/index";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CBAuth, CBDocumentStatusValue, IDossierFormValues} from "../../types";
import { AddRounded, CancelRounded, CheckCircleRounded } from "@mui/icons-material";
import { useHasScope } from "../../../../config/globalConfig";
import { useTranslation } from "react-i18next";
import { getDocumentStatusChipColor } from "../../utils";

interface CustomerBaseDossiersActionsBarProps {
    controlledAttachmentFieldArray: any[],
    disabledAddAttachmentButton: boolean,
    onAdd: () => void,
    updateDossierStatus: (status: string) => void
}

const CustomerBaseDossiersActionsBar = ({
    controlledAttachmentFieldArray,
    disabledAddAttachmentButton,
    onAdd,
    updateDossierStatus

}: CustomerBaseDossiersActionsBarProps) => {

    const hasScope = useHasScope;
    const { t } = useTranslation('customerbase');
    const theme = useTheme();
    const dossierFormContext = useFormContext<IDossierFormValues>();

    const currentDossierStatus = useWatch<IDossierFormValues, "status">({
        name: "status",
        control: dossierFormContext.control
    });

    const originIdentifier = useWatch<IDossierFormValues, "originIdentifier">({
        name: "originIdentifier",
        control: dossierFormContext.control
    });

    const showValidationButton = () => {
        const areAllAttachmentsComplete = controlledAttachmentFieldArray.every(att => att.status == CBDocumentStatusValue.COMPLETE);
        return (
            <>
                <Tooltip title={areAllAttachmentsComplete ? t("tooltips.document.validAsComplete") : t("tooltips.document.allAttachmentCompleteStatusRequired")} arrow>
                    <span>
                        <IconButton
                            color="accent" 
                            disabled={!areAllAttachmentsComplete || currentDossierStatus == CBDocumentStatusValue.COMPLETE}
                            onClick={() => updateDossierStatus(CBDocumentStatusValue.COMPLETE)}
                        >
                            <CheckCircleRounded fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t("tooltips.document.validAsIncomplete")} arrow>
                    <span>
                        <IconButton
                            color="accent"
                            disabled={currentDossierStatus == CBDocumentStatusValue.INCOMPLETE}
                            onClick={() => updateDossierStatus(CBDocumentStatusValue.INCOMPLETE)}
                        >
                            <CancelRounded fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                
            </>
        )
    }
    
    return (
        <Box p={1}>
            <Grid item container md={12} xs={12} alignItems={"center"}>
                <Grid item md={6} xs={6}>
                    <Stack direction={"row"} alignItems={"center"} columnGap={1}>
                        <Controller 
                            control={dossierFormContext.control}
                            name={"template"}
                            render={({field}) => (
                                <Typography variant="h5">
                                    Dossier
                                </Typography>
                            )}
                        />
                        <Controller 
                            control={dossierFormContext.control}
                            name={"status"}
                            render={({field}) => (
                                <Chip
                                    label={t(`documentStatus.${field.value}`)}
                                    sx={{color: "white", backgroundColor: getDocumentStatusChipColor(field.value, theme)}}
                                />
                            )}
                        />
                        {!!originIdentifier && 
                            <Controller 
                                control={dossierFormContext.control}
                                name={"originIdentifier"}
                                render={({field}) => (
                                    <Chip
                                        label={field.value?.split("-")?.at(0)}
                                        sx={{color: "white", backgroundColor: theme.palette.secondary.main}}
                                    />
                                )}
                            />
                        }

                    </Stack>
                </Grid>
                {hasScope(CBAuth.WRITE_CUSTOMER) && 
                    <Grid item md={6} xs={6}>
                        <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"}>
                            {showValidationButton()}
                            <Tooltip title={disabledAddAttachmentButton ? t("message.info.reachedAttachmentsTemplateMaxNum") : ''} arrow>
                                <span>
                                    <IconButton color={"accent"}
                                        disabled={disabledAddAttachmentButton}
                                        onClick={onAdd}
                                    >
                                        <AddRounded />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Grid>
                }
            </Grid>
        </Box>
    )

}

export default CustomerBaseDossiersActionsBar;